import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link2, Unlink, CheckSquare, Square, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useCurrency } from '../../hooks/useCurrency';
import { useReconciliation } from '../../hooks/useReconciliation';
import { usePermissions } from '../../hooks/usePermissions';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import type { ReconciliationLine } from '../../types/reconciliation';

interface ReconciliationPanelProps {
  accountId: string;
  accountCode: string;
  accountName: string;
}

/**
 * Panneau de lettrage intégré dans le Grand Livre.
 * 
 * UX inspirée d'Odoo/Pennylane :
 * - Onglet "Non lettrées" : table avec checkboxes → sélectionner → Lettrer
 * - Onglet "Lettrées" : groupes collapsibles par matchingNumber → Dé-lettrer
 * - Pas de page séparée, pas de surcharge
 */
export function ReconciliationPanel({ accountId, accountCode, accountName }: ReconciliationPanelProps) {
  const { format } = useCurrency();
  const { currentFiscalYear } = useFiscalYearStore();
  const { can } = usePermissions();
  const canManage = can('manage_reconciliation');
  const {
    unreconciledLines,
    unreconciledTotal,
    groups,
    loading,
    error,
    loadUnreconciled,
    loadGroups,
    reconcile,
    unreconcile,
    computeBalance,
  } = useReconciliation();

  const [tab, setTab] = useState<'unreconciled' | 'reconciled'>('unreconciled');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allowPartial, setAllowPartial] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  // Charger les données au montage et quand l'accountId change
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    loadUnreconciled(accountId, { 
      fiscalYearId: currentFiscalYear?.id, 
      page: 1, 
      limit: 50 
    });
    loadGroups(accountId);
  }, [accountId, currentFiscalYear?.id, loadUnreconciled, loadGroups]);

  // Balance de la sélection
  const selectedLines = useMemo(
    () => unreconciledLines.filter(l => selectedIds.has(l.id)),
    [unreconciledLines, selectedIds]
  );
  const selectedBalance = useMemo(() => computeBalance(selectedLines), [selectedLines, computeBalance]);
  const isBalanced = Math.abs(selectedBalance) < 0.01;

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(unreconciledLines.map(l => l.id)));
  }, [unreconciledLines]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Lettrer
  const handleReconcile = async () => {
    if (selectedIds.size < 2) {
      toast.error('Sélectionnez au moins 2 lignes');
      return;
    }
    if (!isBalanced && !allowPartial) {
      toast.error('La balance doit être nulle. Activez le lettrage partiel si nécessaire.');
      return;
    }

    const result = await reconcile(Array.from(selectedIds), allowPartial);
    if (result) {
      toast.success(`Lettrage ${result.matchingNumber} créé (${result.lines.length} lignes)`);
      setSelectedIds(new Set());
      loadUnreconciled(accountId, { fiscalYearId: currentFiscalYear?.id, page, limit: 50 });
      loadGroups(accountId);
    } else if (error) {
      toast.error(error);
    }
  };

  // Dé-lettrer avec confirmation
  const handleUnreconcile = async (matchingNumber: string, lineCount: number) => {
    const confirmed = window.confirm(
      `Dé-lettrer le groupe ${matchingNumber} (${lineCount} ligne(s)) ?\nCette action est réversible — vous pourrez re-lettrer ces lignes.`
    );
    if (!confirmed) return;
    const success = await unreconcile(matchingNumber);
    if (success) {
      toast.success(`Groupe ${matchingNumber} dé-lettré`);
      loadUnreconciled(accountId, { fiscalYearId: currentFiscalYear?.id, page, limit: 50 });
      loadGroups(accountId);
    } else if (error) {
      toast.error(error);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadUnreconciled(accountId, { fiscalYearId: currentFiscalYear?.id, page: newPage, limit: 50 });
  };

  const toggleGroup = (mn: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(mn)) next.delete(mn);
      else next.add(mn);
      return next;
    });
  };

  const allSelected = unreconciledLines.length > 0 && unreconciledLines.every(l => selectedIds.has(l.id));
  const groupEntries = Object.entries(groups);
  const totalPages = Math.ceil(unreconciledTotal / 50);

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <Link2 className="h-5 w-5 text-primary" />
          <div>
            <span className="font-semibold">Lettrage</span>
            <span className="ml-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
              {accountCode}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">{accountName}</span>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Onglets */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setTab('unreconciled')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'unreconciled'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Non lettrées
            {unreconciledTotal > 0 && (
              <span className="ml-2 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                {unreconciledTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('reconciled')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'reconciled'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Lettrées
            {groupEntries.length > 0 && (
              <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">
                {groupEntries.length}
              </span>
            )}
          </button>
        </div>

        {/* ───── Onglet : lignes non lettrées ───── */}
        {tab === 'unreconciled' && (
          <div className="space-y-3">
            {/* Barre d'action */}
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-medium">{selectedIds.size} sélectionnée(s)</span>
                  <span className={`font-mono font-semibold ${isBalanced ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    Balance : {format(selectedBalance)}
                  </span>
                  {!isBalanced && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                </div>
                <div className="flex items-center gap-2">
                  {!isBalanced && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" title="Autoriser le lettrage même si la balance n'est pas nulle">
                      <input
                        type="checkbox"
                        checked={allowPartial}
                        onChange={e => setAllowPartial(e.target.checked)}
                        className="w-3.5 h-3.5 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      Lettrage partiel
                    </label>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Link2}
                    onClick={handleReconcile}
                    disabled={loading || selectedIds.size < 2 || (!isBalanced && !allowPartial) || !canManage}
                  >
                    Lettrer
                  </Button>
                </div>
              </div>
            )}

            {/* Indication pour l'utilisateur */}
            {selectedIds.size === 0 && unreconciledLines.length > 0 && (
              <p className="text-xs text-muted-foreground italic">
                Cliquez sur les lignes à rapprocher, puis appuyez sur « Lettrer » quand la balance est à zéro.
              </p>
            )}

            {/* Table */}
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="w-10 px-3 py-2.5">
                      <button
                        onClick={allSelected ? clearSelection : selectAll}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {allSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Réf.</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Libellé</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Débit</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Crédit</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && unreconciledLines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          Chargement...
                        </div>
                      </td>
                    </tr>
                  ) : unreconciledLines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground italic">
                        Toutes les lignes sont lettrées
                      </td>
                    </tr>
                  ) : (
                    unreconciledLines.map(line => {
                      const selected = selectedIds.has(line.id);
                      return (
                        <tr
                          key={line.id}
                          onClick={() => toggleSelect(line.id)}
                          className={`border-b border-border/50 cursor-pointer transition-colors ${
                            selected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/30'
                          }`}
                        >
                          <td className="w-10 px-3 py-2">
                            {selected
                              ? <CheckSquare className="w-4 h-4 text-primary" />
                              : <Square className="w-4 h-4 text-muted-foreground" />}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {line.date ? new Date(line.date).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{line.reference || '—'}</td>
                          <td className="px-3 py-2">{line.description}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {line.debit ? <span className="text-green-600 dark:text-green-400">{format(line.debit)}</span> : ''}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {line.credit ? <span className="text-red-600 dark:text-red-400">{format(line.credit)}</span> : ''}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{unreconciledTotal} ligne(s)</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>
                    Préc.
                  </Button>
                  <span className="px-2 py-1">{page}/{totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
                    Suiv.
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ───── Onglet : groupes lettrés ───── */}
        {tab === 'reconciled' && (
          <div className="space-y-2">
            {groupEntries.length === 0 ? (
              <p className="text-center text-muted-foreground italic py-6">Aucun lettrage pour ce compte</p>
            ) : (
              groupEntries.map(([mn, lines]) => {
                const expanded = expandedGroups.has(mn);
                const balance = computeBalance(lines);
                return (
                  <div key={mn} className="border border-border rounded-lg overflow-hidden">
                    {/* En-tête du groupe */}
                    <div
                      onClick={() => toggleGroup(mn)}
                      className="flex items-center justify-between px-4 py-2.5 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <Badge variant="success">{mn}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {lines.length} ligne(s)
                        </span>
                        {Math.abs(balance) >= 0.01 && (
                          <Badge variant="warning">Partiel : {format(balance)}</Badge>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={Unlink}
                        onClick={e => { e.stopPropagation(); handleUnreconcile(mn, lines.length); }}
                        disabled={loading || !canManage}
                      >
                        Dé-lettrer
                      </Button>
                    </div>
                    {/* Détail des lignes */}
                    {expanded && (
                      <table className="w-full text-sm">
                        <tbody>
                          {lines.map((line: ReconciliationLine) => (
                            <tr key={line.id} className="border-t border-border/50">
                              <td className="px-4 py-1.5 text-xs text-muted-foreground w-24">
                                {line.date ? new Date(line.date).toLocaleDateString('fr-FR') : ''}
                              </td>
                              <td className="px-3 py-1.5 font-mono text-xs w-24">{line.reference || ''}</td>
                              <td className="px-3 py-1.5">{line.description}</td>
                              <td className="px-3 py-1.5 text-right font-mono w-28">
                                {line.debit ? <span className="text-green-600 dark:text-green-400">{format(line.debit)}</span> : ''}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono w-28">
                                {line.credit ? <span className="text-red-600 dark:text-red-400">{format(line.credit)}</span> : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
